import { FC, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useNotificationService } from '../notification';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import ModalContainer from '../UIElements/modal-container';
import { Box, Button, Stack, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import useResponsive from '../../hooks/useResponsive';
import { labelStyles } from '../UIElements/styles-input';
import { textSty } from '../UIElements/styles-typography';
import { formatDateFromString } from '../../utils/url-id';
import { apiApproveMedicRequest } from '../../api/requests';

export const RequestModal: FC<{
  open: boolean;
  content: any;
  handleModal: () => void;
  handleRefresh: () => void;
}> = ({ open, handleModal, content, handleRefresh }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState({ approve: false, disapprove: false });
  const { showNotification } = useNotificationService();

  const isMedium = useResponsive('down', 'md', 'xl');

  const mutation = useMutation({
    mutationFn: (fieldValues: { id: string; token: string; status: string }) =>
      apiApproveMedicRequest(fieldValues),
    onSuccess: (data) => {
      if (data?.status === 'error') {
        if ('msg' in data) {
          showNotification(data?.msg ?? '', 'error');
          setLoading({ approve: false, disapprove: false });
          return;
        }
        return;
      }
      if ('data' in data) {
        setLoading({ approve: false, disapprove: false });
        handleRefresh();
        handleModal();
      }
    },
    onError: (error) => {
      showNotification('Fetch process failed', 'error');
      setLoading({ approve: false, disapprove: false });
    },
  });
  const handleApprove = () => {
    const data = {
      token,
      id: content?._id,
      status: 'approved',
    };
    setLoading({ approve: true, disapprove: false });
    mutation.mutate(data);
  };
  const handleDisapprove = () => {
    const data = {
      token,
      id: content?._id,
      status: 'disapproved',
    };
    setLoading({ approve: false, disapprove: true });
    mutation.mutate(data);
  };
  return (
    <ModalContainer
      sx={{
        borderRadius: '16px',
      }}
      size={'md'}
      openModal={open}
      fullWidth={true}
      handleModal={handleModal}
    >
      <Box
        sx={{
          background: '#fff',
          padding: 3,
          borderRadius: '16px',
        }}
      >
        <Stack>
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'Outfit',
              fontWeight: 500,
              color: '#2F66F4',
              marginBottom: 3,
            }}
          >
            Employee Request
          </Typography>
          <Box>
            <Stack
              direction={isMedium ? 'column' : 'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>Title</Typography>
                <Typography sx={{ ...textSty }}>{content?.title}</Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>Type</Typography>
                <Typography sx={{ ...textSty }}>{content?.type}</Typography>
              </Stack>
            </Stack>
            <Stack
              direction={isMedium ? 'column' : 'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Total Days
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {content?.total_days}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Requested On
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatDateFromString(content?.created_at)}
                </Typography>
              </Stack>
            </Stack>
            <Stack
              direction={isMedium ? 'column' : 'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Start Date
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatDateFromString(content?.start_date)}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>End Date</Typography>
                <Typography sx={{ ...textSty }}>
                  {formatDateFromString(content?.end_date)}
                </Typography>
              </Stack>
            </Stack>

            <Stack
              direction={isMedium ? 'column' : 'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Medic Name
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {content?.medic_info?.name}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Medic email
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {content?.medic_info?.email}
                </Typography>
              </Stack>
            </Stack>
            <Stack
              direction={isMedium ? 'column' : 'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Medic Type
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {content?.medic_info?.medic_type}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>Status</Typography>
                <Typography sx={{ ...textSty }}>{content?.status}</Typography>
              </Stack>
            </Stack>
          </Box>

          <Stack
            direction="row"
            spacing={3}
            justifyContent={'flex-end'}
            sx={{ mt: 3 }}
          >
            <Button
              variant="outlined"
              onClick={handleModal}
              sx={{ borderRadius: '8px' }}
            >
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              loading={loading.disapprove}
              color="error"
              sx={{ borderRadius: '6px' }}
              onClick={handleDisapprove}
            >
              Disapprove Request
            </LoadingButton>
            <LoadingButton
              variant="contained"
              loading={loading.approve}
              color="primary"
              onClick={handleApprove}
              sx={{ borderRadius: '6px' }}
            >
              Approve Request
            </LoadingButton>
          </Stack>
        </Stack>
      </Box>
    </ModalContainer>
  );
};
